import React from 'react';

import { Settings } from '@mui/icons-material';
import { Box, IconButton, List, Typography } from '@mui/material';
import { isNotNil, noop } from 'es-toolkit';
import { FormState } from 'final-form';
import { useLocation } from 'react-router';

import { FullscreenLoader } from '@hrlogics/react-components';
import { Field, Form, FormSpy } from '@hrlogics/react-components/form';

import { Nullable } from '@clearverify/types';

import { NavListItem } from 'src/components/common';
import { DemoPrivacyText } from 'src/components/demoPrivacyMode';
import { AvatarImageUploadField } from 'src/components/finalForm';
import { DroppedFile } from 'src/types/file';
import { MenuItem } from 'src/types/menu';
import { getIsPathMatchLocation } from 'src/utils/router';

import { FormField } from './constants';
import * as styles from './styles';
import { FormValues } from './types';

type ItemDetailsLayoutProps = {
  name: Nullable<string>;
  subtitle?: Nullable<string>;
  subtitleComponent?: React.ReactNode;
  logoUrl: string;
  menuItems: Array<Nullable<MenuItem>>;
  logoProps: {
    loading: boolean;
    onChange: (image: DroppedFile) => Promise<void> | void;
  };
  loading?: boolean;
  onSettingsClick?: () => void;
  sidebarFooter?: React.ReactNode;
};

export const ItemDetailsLayout: React.FC<React.PropsWithChildren<ItemDetailsLayoutProps>> = ({
  children,
  name,
  subtitle,
  subtitleComponent,
  logoUrl,
  menuItems,
  logoProps,
  loading,
  onSettingsClick,
  sidebarFooter,
}) => {
  const location = useLocation();

  const getIsCurrentPath = React.useCallback(
    (path: string) => {
      return getIsPathMatchLocation(location, path);
    },
    [location],
  );

  const menuItemsList = React.useMemo(() => {
    return menuItems
      .filter(isNotNil)
      .filter(menuItem => !menuItem.hide)
      .map(menuItem => {
        const isSelectedByLinkMatch = getIsCurrentPath(menuItem.link);

        return (
          <NavListItem
            key={menuItem.link}
            sx={styles.menuListItemStyles}
            link={menuItem.link}
            selected={menuItem.isSelected || isSelectedByLinkMatch}
            dense={false}
            disableGutters={false}
            divider={menuItem.divider}
            startIcon={<NavListItem.Icon sx={styles.menuListItemIconStyles} icon={menuItem.icon} />}
            listItemText={<NavListItem.Text>{menuItem.title}</NavListItem.Text>}
            disabled={menuItem.disabled}
          />
        );
      });
  }, [getIsCurrentPath, menuItems]);

  const handleAvatarChange = React.useCallback(
    (formState: FormState<FormValues>) => {
      const avatarFile = formState.values[FormField.avatarFile];
      if (!avatarFile) return;
      logoProps.onChange(avatarFile);
    },
    [logoProps],
  );

  if (loading) {
    return <FullscreenLoader sx={{ height: '500px' }} />;
  }

  return (
    <Box sx={styles.containerStyles}>
      <Box component="aside" sx={styles.sidebarContainerStyles}>
        <Box sx={styles.userInfoBlockStyles}>
          <Form<FormValues> onSubmit={noop}>
            <Field
              component={AvatarImageUploadField}
              name={FormField.avatarFile}
              defaultImageUrl={logoUrl}
              avatarProps={{
                sx: styles.avatarStyles,
              }}
              loading={logoProps.loading}
            />
            <FormSpy subscription={{ values: true }} onChange={handleAvatarChange} />
          </Form>

          <Typography sx={styles.nameStyles}>
            <DemoPrivacyText>{name}</DemoPrivacyText>
          </Typography>

          {subtitleComponent}

          {subtitle && (
            <Typography variant="caption" align="inherit" color="textSecondary">
              {subtitle}
            </Typography>
          )}

          {onSettingsClick && (
            <Box sx={styles.settingsButtonContainerStyles}>
              <IconButton onClick={onSettingsClick}>
                <Settings />
              </IconButton>
            </Box>
          )}

          {sidebarFooter}
        </Box>

        <List sx={styles.menuItemsListStyles}>{menuItemsList}</List>
      </Box>

      <Box sx={styles.contentContainerStyles}>{children}</Box>
    </Box>
  );
};
